import React, { useCallback } from 'react';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import classNames from 'classnames';

import ErrorLabel from '../common/Labels/ErrorLabel';
import Label from '../common/Labels/Label';
import LoadingSpinner from '../common/LoadingSpinner/LoadingSpinner';
import VerticalGradientOverlay from '../common/overlays/VerticalGradientOverlay';

import { AuditLogEntryType } from '../../services/logs_service';

interface LogRowDataType {
  logs: AuditLogEntryType[],
  selectedTimestamp: number | undefined,
  isItemLoaded: (index: number) => boolean,
  onLogSelected: (item: AuditLogEntryType | null) => void,
}

interface LogRowProps {
  index: number,
  style: React.CSSProperties,
  data: LogRowDataType
}

const LogRow = ({ 
  index, 
  style,
  data
}: LogRowProps) => {
  const {
    logs,
    selectedTimestamp,
    onLogSelected,
    isItemLoaded
  } = data;
  const logEntry = logs[index];

  const ts = logEntry.formattedDateTime.slice(0, 23).replace('T', ' ');
  const isSelected = selectedTimestamp === logEntry?.unixTime;
  const onClick = useCallback(() => {
    if (isSelected) {
      onLogSelected(null);
    } else {
      onLogSelected(logEntry)
    }
  }, [logEntry, isSelected, onLogSelected]);

  if (!isItemLoaded(index)) {
    return (
      <div className="flex flex-row p-4 bg-white border border-border-dark">
        <ErrorLabel>Loading Item </ErrorLabel>
        <LoadingSpinner animationKey="log-list-loader" className="ml-1" size="small" />
      </div>
    )
  }

  return (
    <button
      id={`audit-log-${index}`}
      type="button"
      style={style}
      onClick={onClick}
      className={classNames(
        'p-4 cursor-pointer border border-border-dark',
        { 'bg-white hover:bg-border-light': !isSelected },
        { 'bg-border-dark': isSelected },
      )}>
      <Label>{ts}</Label>
    </button>
  )
}

interface LogSelectionListProps {
  height: number,
  width: number,
  logs: AuditLogEntryType[],
  nextPageToken: number | null,
  selectedTimestamp: number | undefined,
  onLogSelected: Function,
  handleLoadNextPage: (startIndex: number, stopIndex: number) => void | Promise<void>,
}

export const LogSelectionList = ({
  height,
  width,
  logs,
  nextPageToken,
  selectedTimestamp,
  onLogSelected,
  handleLoadNextPage,
}: LogSelectionListProps) => {
  const itemCount = nextPageToken ? logs.length + 1 : logs.length;

  // Every row is loaded except for our loading indicator row.
  const isItemLoaded = (index: number) => !nextPageToken || index < logs.length;

  return (
    <div>
      <VerticalGradientOverlay className="top-0 left-0 right-0 mx-0.5" fromDirection="b" />
      <InfiniteLoader
        isItemLoaded={isItemLoaded}
        itemCount={itemCount}
        loadMoreItems={handleLoadNextPage}
        minimumBatchSize={10}
        threshold={10}
      >
        {({ onItemsRendered, ref }) => (
          <FixedSizeList
            ref={ref}
            height={height}
            onItemsRendered={onItemsRendered}
            itemCount={logs.length}
            itemSize={64}
            width={width}
            itemData={{
              logs,
              selectedTimestamp,
              onLogSelected,
              isItemLoaded
            } as any}
          >
            {LogRow}
          </FixedSizeList>
        )}
      </InfiniteLoader>
      <VerticalGradientOverlay className="bottom-0 left-0 right-0 mx-0.5" fromDirection="t" />
    </div>
  )
};

export default LogSelectionList;
